/**
 * AppShell — mise en page pleine page :
 *  - landing, recherche, profil public, /connexion, /inscription, /pro/onboarding :
 *    plein écran sans chrome applicatif ;
 *  - espaces /pro, /app, /admin : Header + Sidebar (navigation par rôle).
 *
 * La navigation reflète la session (rôle réel), jamais un sélecteur fictif.
 */
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import { Box } from '@mui/material';
import { Header } from '../components/layout/Header';
import { Sidebar } from '../components/layout/Sidebar';
import { MobileBottomNav } from '../components/layout/MobileBottomNav';
import { GlobalOverlaysProvider, useGlobalOverlays } from '../components/common/GlobalOverlaysProvider';
import { AppRoutes } from './router';
import { ROUTES } from '../config/routes';
import { routeToTab } from '../config/navMapping';
import type { RootState } from './store';
import { toggleColorMode } from '../features/ui/ui.slice';
import { CallLauncherProvider } from '../features/calls/hooks/useCallLauncher';
import { canAccessSpace } from '../lib/auth/permissions';
import { useListNotificationsQuery } from '../lib/api/baseApi';

/** Onglets accessibles à un client (espace /app) — la Sidebar l'utilise aussi. */
export const CLIENT_TABS: string[] = ['dashboard', 'marketplace', 'calls', 'profile', 'settings'];

/** Onglets d'un visiteur non connecté : accueil + recherche partagée + profil. */
export const GUEST_TABS: string[] = ['landing', 'marketplace', 'profile', 'settings'];

/** Routes affichées sans chrome applicatif (pleine page). */
const FULL_PAGE_PREFIXES = ['/connexion', '/inscription', '/appel/', '/docs'];
const FULL_PAGE_PATHS = ['/', ROUTES.pro.onboarding];

function isFullPage(pathname: string): boolean {
  if (FULL_PAGE_PREFIXES.some((p) => pathname.startsWith(p))) return true;
  if (FULL_PAGE_PATHS.includes(pathname)) return true;
  // Profil public.
  if (pathname.startsWith('/professionnels/')) return true;
  return false;
}

/** Pages sans sider navbar : auth et onboarding uniquement — la recherche,
 *  la page tenant et les espaces gardent le rail permanent. */
function isFullPageNoSider(pathname: string): boolean {
  if (FULL_PAGE_PREFIXES.some((p) => pathname.startsWith(p))) return true;
  if (FULL_PAGE_PATHS.includes(pathname)) return true;
  return false;
}

export default function AppShell() {
  return (
    <CallLauncherProvider>
      <GlobalOverlaysProvider>
        <AppFrame />
      </GlobalOverlaysProvider>
    </CallLauncherProvider>
  );
}

function AppFrame() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const colorMode = useSelector((s: RootState) => s.ui.colorMode);
  const { openNotifications, openMessages } = useGlobalOverlays();
  const role = useSelector((s: RootState) => s.session.user?.role);
  const isProRole = role !== undefined && canAccessSpace(role, 'pro');
  const isClientRole = role === 'client';
  const isAdminRole = role !== undefined && canAccessSpace(role, 'admin');

  const fullPage = isFullPage(location.pathname);
  const currentTab = routeToTab(location.pathname);
  /** Visiteur non connecté : navigation Header + bottom nav sans espace pro. */
  const isGuest = !role;

  // Notifications in-app : badge Header alimenté par l'API (skip si visiteur).
  const { data: notifications } = useListNotificationsQuery(undefined, { skip: isGuest });
  const unreadNotifications = notifications?.filter((n) => !n.read).length ?? 0;

  useEffect(() => {
    import('../lib/observability/logger').then(({ logger }) =>
      logger.info('route.change', { path: location.pathname }),
    );
  }, [location.pathname]);

  const handleSelectTab = (tab: string) => {
    switch (tab) {
      case 'dashboard':
        navigate(isAdminRole ? ROUTES.admin.root : isClientRole ? ROUTES.app.root : ROUTES.pro.dashboard);
        break;
      case 'marketplace':
      case 'search':
        navigate(ROUTES.search);
        break;
      case 'profile':
        // Profil selon l'espace : /app/profil (client), /pro/profil (pro), public sinon.
        navigate(isClientRole ? ROUTES.app.profile : ROUTES.pro.profile);
        break;
      case 'calls':
        // Journal des appels, décliné par rôle (pro, client) — migr. 0023.
        navigate(isClientRole ? ROUTES.app.calls : ROUTES.pro.calls);
        break;
      case 'voice-agent':
        navigate(ROUTES.pro.voiceAgent);
        break;
      case 'pipeline':
        navigate(ROUTES.pro.leads);
        break;
      case 'requests':
        navigate(ROUTES.app.requests);
        break;
      case 'favorites':
        navigate(ROUTES.app.favorites);
        break;
      case 'notifications':
        navigate(ROUTES.app.notifications);
        break;
      case 'services':
        navigate(ROUTES.pro.services);
        break;
      case 'quota':
        navigate(ROUTES.pro.usage);
        break;
      case 'statistics':
        navigate(ROUTES.pro.statistics);
        break;
      case 'subscription':
        navigate(ROUTES.pro.subscription);
        break;
      case 'portfolio':
        navigate(ROUTES.pro.portfolio);
        break;
      case 'calendar':
        navigate(ROUTES.pro.calendar);
        break;
      case 'admin':
        navigate(ROUTES.admin.root);
        break;
      case 'onboarding':
        navigate(ROUTES.pro.onboarding);
        break;
      case 'settings':
        navigate(ROUTES.pro.settings);
        break;
      case 'landing':
        navigate(isClientRole ? ROUTES.app.root : isProRole ? ROUTES.pro.dashboard : isAdminRole ? ROUTES.admin.root : ROUTES.home);
        break;
      default:
        navigate(ROUTES.home);
    }
  };

  // Sider navbar présente sur toutes les pages sauf auth/onboarding,
  // y compris la recherche et le profil public du tenant.
  const showSider = !isFullPageNoSider(location.pathname);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'background.default',
        display: 'flex',
        flexDirection: 'row',
        transition: 'background-color 0.25s ease',
      }}
    >
      {showSider && (
        <Sidebar
          currentTab={currentTab}
          onSelectTab={handleSelectTab}
          colorMode={colorMode}
          onToggleColorMode={() => dispatch(toggleColorMode())}
          visibleTabs={isClientRole ? CLIENT_TABS : isGuest ? GUEST_TABS : undefined}
        />
      )}

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {!fullPage && (
          <Header
            currentTab={currentTab}
            onSelectTab={handleSelectTab}
            unreadNotifications={unreadNotifications}
            unreadMessages={1}
            onOpenNotifications={openNotifications}
            onOpenMessages={openMessages}
            isAdmin={isAdminRole}
          />
        )}

        <Box
          component="main"
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            // Full-bleed : les pages pleine page (landing, auth, docs, appel)
            // gèrent leur propre fond — le padding laissait transparaître les
            // bandes crème du body autour du hero (« bordures blanches »).
            ...(fullPage
              ? { p: 0 }
              : {
                  px: { xs: 2, md: 3 },
                  pt: { xs: 2, md: 3 },
                  pb: { xs: '76px', md: 3 },
                }),
          }}
        >
          <AppRoutes />
        </Box>
      </Box>

      {!fullPage && (
        <MobileBottomNav
          currentTab={currentTab}
          onSelectTab={handleSelectTab}
          isPro={isProRole}
          isAdmin={isAdminRole}
          onDirectCall={undefined}
        />
      )}
    </Box>
  );
}
